<template>
  <div class="exact-product">
    <div class="exact-product-query">
      <!-- 产品名称 -->
      <ProductNameSelect
        class="exact-product-condition"
        :defaultName="productName"
        @change="handleProductNameSelectChange"
      />

      <!-- 营销场景 -->
      <MarketingTypeSelect
        v-model="marketingType"
        class="exact-product-condition"
        @change="handleQuery"
      />

      <!-- 汇总时间 -->
      <SummaryDateSelect
        v-model="summaryDate"
        class="exact-product-condition"
        @change="handleSummaryDateChange"
      />

      <!-- 对比时间 -->
      <ContrastDatePicker
        class="exact-product-condition"
        :isContrast.sync="isDatePickerContrast"
        :defaultDate="baseDate"
        @change="handleContrastDatePickerChange"
      />

      <!-- 查询 -->
      <Button
        class="exact-product-condition"
        type="primary"
        @click="handleQuery"
      >
        查询
      </Button>
    </div>

    <div>
      <Title text="图表展示" />

      <Tabs
        v-model="tab"
        class="exact-product-tabs"
        @tab-click="handleTabsChange"
      >
        <TabPane
          label="趋势分析"
          name="trend"
        >
          <ContrastSelect
            v-model="trendOptions"
            :baseList="fieldList"
            :contrastList="fieldList"
            :isContrast.sync="isTrendContrast"
            :defaultContrast="'served_request'"
            @change="handleTrendContrastSelectChange"
          />
        </TabPane>

        <TabPane
          label="占比分析"
          name="proportion"
        >
          <span>汇总维度：</span>
          <Select
            v-model="proportionDimension"
            style="margin-right: 20px"
            @change="handleProportionDimensionChange"
          >
            <Option
              v-for="item in dimensionList"
              :key="item.key"
              :label="item.name"
              :value="item.key"
            />
          </Select>

          <span>展示指标：</span>
          <Select
            v-model="trendOptions[0]"
            @change="handleProportionFieldChange"
          >
            <Option
              v-for="item in fieldList"
              :key="item.key"
              :label="item.name"
              :value="item.key"
            />
          </Select>

          <RadioGroup
            v-model="proType"
            style="float: right"
            @change="handleProTypeChange"
          >
            <RadioButton label="pie">饼图</RadioButton>
            <RadioButton label="line">折线图</RadioButton>
          </RadioGroup>
        </TabPane>
      </Tabs>

      <div v-loading="chartLoading">
        <Chart
          class="exact-product-chart"
          :dataSource="rawChartData"
          :type="tab"
          :field="trendOptions"
          :dimension="proportionDimension"
          :proType="proType"
        />
      </div>
    </div>

    <div class="exact-product-table">
      <Title text="详细数据" />

      <div v-loading="tableLoading">
        <CustomTable
          class="product-report-detail-table"
          topic="product_precision_detail"
          :args="downloadArgs"
          :dataSource="rawTableData"
          :customColumns="customColumns"
          :fixedColumns="fixedColumns"
          @created="handleCustomTableCreated"
          @download="handleCustomTableDownload"
          @change-columns="handleCustomTableColumnsChange"
          @sort-change="handleCustomTableSortChange"
          @filter-x-change="handleCustomTableFilterChange"
          @current-change="handleCustomTableCurrentChange"
          @size-change="handleCustomTableSizeChange"
        >
          <template v-slot:column="{ data: d }">
            <Tooltip v-if="checkIsHover(d.field.key, d)">
              <div slot="content">
                <div>对比日期：{{ d.value.before_date }}</div>
                <div>对比日期的值：{{ d.value.before_value }}</div>
                <div>差值：{{ d.value.diff_value }}</div>
              </div>
              <div>
                <span>{{ d.value.value }}</span>
                <br>
                <span :class="`exact-product-${checkRate(d.value.rate)}`">
                  ({{ d.value.rate }})
                </span>
              </div>
            </Tooltip>

            <span v-else>{{ d.value }}</span>
          </template>
        </CustomTable>
      </div>
    </div>
  </div>
</template>

<script>
import {
  Button,
  Tabs,
  TabPane,
  Select,
  Option,
  Tooltip,
  RadioGroup,
  RadioButton,
} from 'element-ui';
import Moment from 'moment';

import Title from '@/components/Title';
import SummaryDateSelect from '@/components/SummaryDateSelect/Index';
import MarketingTypeSelect from '@/components/MarketingTypeSelect';
import ContrastDatePicker from '@/components/DatePicker/Contrast';
import ContrastSelect from '@/components/ContrastSelect/Index';
import CustomTable from '@/components/CustomTable/Index';
import ProductNameSelect from './components/ProductNameSelect/Index';
import Chart from './components/Chart/Index';

import shortcuts from '@/config/shortcuts';
import {
  fields,
  dimension,
  customColumns,
  fixedColumns,
  fieldWithDiff,
} from './config/';
import { getFieldsByList } from '@/config/columns/';
import requestMixin from './mixins/request';
import * as $cache from '@/common/cache';

export default {
  components: {
    Button,
    Title,
    Tabs,
    TabPane,
    Select,
    Option,
    Tooltip,
    RadioGroup,
    RadioButton,

    SummaryDateSelect,
    MarketingTypeSelect,
    ContrastDatePicker,
    ProductNameSelect,
    ContrastSelect,
    Chart,
    CustomTable,
  },

  mixins: [requestMixin],

  data() {
    const { id, name, start, end } = this.$route.query;

    return {
      // 产品id
      productId: id,
      // 产品名称
      productName: name,
      // 营销场景
      marketingType: '',
      // 汇总时间
      summaryDate: 'day',
      // 是否开启时间对比
      isDatePickerContrast: false,
      // 基础时间
      baseDate: [Moment(start), Moment(end)] || shortcuts.last7Days.data,
      // 对比时间
      contrastDate: null,

      // 当前tab
      tab: 'trend',
      // 趋势指标对比数据
      trendOptions: ['cost_total'],
      // 是否开启趋势对比
      isTrendContrast: false,
      // 指标列表
      fieldList: getFieldsByList(fields),
      // 维度列表
      dimensionList: getFieldsByList(dimension),
      // 占比维度
      proportionDimension: 'unit',
      // 图表 loading
      chartLoading: false,
      // 图表原始数据
      rawChartData: {},
      // 趋势图类型
      proType: 'pie',

      // 表格 loading
      tableLoading: false,
      // 自定义列
      customColumns,
      // 固定列
      fixedColumns,
      // 表格数据
      rawTableData: {},
      // 当前页码
      page: 1,
      // 每页数量
      pageSize: 10,
      // 排序
      order: 'day desc',
      // 过滤
      rawfilters: {},
      // 指标列表
      fields: [],
    };
  },

  computed: {
    filters() {
      const arr = [];
      Object.keys(this.rawfilters).forEach((key) => {
        const { type, min, max } = this.rawfilters[key];
        arr.push(`${type},${min},${max}`);
      });
      return arr;
    },

    downloadArgs() {
      const params = this.getParams();
      params.order_by = this.order;
      params.fields = this.fields.join(',');
      params.filters = this.filters;
      params.dimension = this.summaryDate;
      params.page = this.page;
      params.pageSize = this.pageSize;
      params._login_token = ($cache.localGet($cache.AUTH) || {}).token || '';
      return params;
    },
  },

  created() {
    this.queryChartData();
  },

  methods: {
    // 选择产品
    handleProductNameSelectChange(id) {
      this.productId = id;
      this.handleQuery();
    },

    // 选择汇总时间
    handleSummaryDateChange() {
      this.handleQuery();
    },

    // 选择对比时间
    handleContrastDatePickerChange([start, end]) {
      this.page = 1;
      this.baseDate = start;
      this.contrastDate = end;

      if (end) {
        this.isTrendContrast = false;
        this.trendOptions[1] = '';
      }

      this.handleQuery();
    },

    // 查询
    handleQuery() {
      this.page = 1;
      this.queryChartData();
      this.queryTableData();
    },

    // 切换 Tab
    handleTabsChange() {
      this.queryChartData();
    },

    // 趋势图类型切换
    handleProTypeChange() {
      this.queryChartData();
    },

    // 选择趋势对比指标
    handleTrendContrastSelectChange(data) {
      if (data[1]) {
        this.isDatePickerContrast = false;
        this.contrastDate = null;
      }

      this.queryChartData();
    },

    // 选择占比维度
    handleProportionDimensionChange() {
      this.queryChartData();
    },

    // 选择占比指标
    handleProportionFieldChange() {
      this.queryChartData();
    },

    handleCustomTableCreated(fields) {
      this.page = 1;
      this.fields = fields;
      this.queryTableData();
    },

    // 表格下载
    handleCustomTableDownload() {
      this.download();
    },

    // 变更自定义列
    handleCustomTableColumnsChange(fields) {
      this.page = 1;
      this.fields = fields;
      this.queryTableData();
    },

    // 更改排序
    handleCustomTableSortChange(sort) {
      if (sort) {
        this.page = 1;
        this.order = sort;
        this.queryTableData();
      }
    },

    // 更改过滤
    handleCustomTableFilterChange(filter) {
      this.page = 1;
      const { type, min, max } = filter;
      if (min !== undefined && max !== undefined) {
        this.$set(this.rawfilters, type, filter);
      } else {
        this.$delete(this.rawfilters, type);
      }
      this.queryTableData();
    },

    // 更改页码
    handleCustomTableCurrentChange(current) {
      this.page = current;
      this.queryTableData();
    },

    // 更改每页数量
    handleCustomTableSizeChange(size) {
      this.pageSize = size;
      this.queryTableData();
    },

    // 当前单元格是否可以悬浮
    checkIsHover(key, d) {
      return fieldWithDiff.includes(key) && d.value;
    },

    // 判断是上涨还是下跌
    checkRate(rate) {
      return rate.indexOf('-') !== -1 ? 'bottom' : 'top';
    },

    // 查询图表数据
    queryChartData() {
      if (this.productId) {
        this.chartLoading = true;
        if (this.tab === 'trend') {
          this.queryPrecisionLine();
        } else if (this.tab === 'proportion') {
          this.queryPrecisionPie();
        }
      }
    },

    // 查询表格数据
    queryTableData() {
      if (this.productId) {
        this.tableLoading = true;
        this.queryPrecisionList();
      }
    },
  },
};
</script>

<style lang="less" scoped>
.exact-product {
  font-size: 14px;

  &-query {
    margin-bottom: 32px;
  }

  &-condition {
    margin: 0 20px 20px 0;
  }

  &-tabs {
    margin-top: 20px;
  }

  &-chart {
    margin-top: 20px;
  }

  &-table {
    margin-top: 32px;
  }

  &-top {
    color: @crm-rise-color;
  }

  &-bottom {
    color: @crm-fall-color;
  }
}
</style>